import type React from 'react'
import type { ChangeEvent } from 'react'
import { useEffect, useMemo, useState } from 'react'
import type { FileForProcessing, IOrder, ISamplePool } from '../../../Model/index.js'
import { SampleConfigurationTypes } from '../../../Generated/SampleConfigurationType.js'
import { FilterTypes } from '../../../Generated/FilterType.js'
import { Environment } from '../../../environment.js'
import { tracingAxios } from '../../../Repository/Sentry.js'
import { useDispatch } from 'react-redux'
import Actions from '../../../Flux/index.js'
import { orderSlice } from '../../../Flux/Slice/Order/OrderSlice.js'
import { validate as isValidUUID } from 'uuid'
import { useGetAllOrders, useGetSamplePoolSelector } from 'Flux/Selector/index.js'
import { AmpliconWidget } from './AmpliconWidget.tsx'
import type { SingleValue } from 'react-select'
import Select from 'react-select'
import type { BaseModalProps } from '../../Modal/BaseModal.js'
import { BaseModal } from '../../Modal/BaseModal.js'

export interface ImportModalProps {
  order: IOrder
  samplePools: ISamplePool[]
}

export const ImportSamplesModal = (props: ImportModalProps & BaseModalProps) => {
  const [orderIdToImport, setOrderIdToImport] = useState<string>('')
  const [inProgress, setInProgress] = useState<boolean>(false)
  const [samplePools, setSamplePools] = useState<ISamplePool[]>([])
  const [selectedSamples, setSelectedSamples] = useState<Record<string, boolean>>({})
  const [selectedNCs, setSelectedNCs] = useState<Record<string, boolean>>({})
  const [error, setError] = useState<string | null>(null)
  const [isImportCompleted, setIsImportCompleted] = useState<boolean>(false)
  const dispatch = useDispatch()
  const orders = useGetAllOrders()
  /* the pools as they are now, not as they were when the modal opened: one may have been added or deleted */
  const storedPools = useGetSamplePoolSelector()
  const targetPools = storedPools === 'LOADING' ? props.samplePools : storedPools
  const [importAsNewSamplePool, setImportAsNewSamplepool] = useState(props.samplePools.length == 0)
  const [targetSamplePool, setTargetSamplePool] = useState<number>()
  const [filterByTaxa, setFilterByTaxa] = useState<boolean>(true)
  const [orderSearch, setOrderSearch] = useState<string>('')
  const [debouncedOrderSearch, setDebouncedOrderSearch] = useState<string>('')
  const [copyPrimers, setCopyPrimers] = useState<boolean>(false)
  const [copyMetadata, setCopyMetadata] = useState<boolean>(false)
  const [acknowledgeModeConversion, setAcknowledgeModeConversion] = useState<boolean>(false)

  // Samples are always imported using THIS order's sample format, not the source order's.
  // (Keep in sync with SamplePoolWidget.handleFileInput.)
  const targetIsTrimmed = SampleConfigurationTypes[props.order.sampleConfiguration]?.impliesTrimmed ?? false
  const targetIsMerged = SampleConfigurationTypes[props.order.sampleConfiguration]?.impliesMerged ?? false

  const targetLabel = SampleConfigurationTypes[props.order.sampleConfiguration]?.label ?? props.order.sampleConfiguration
  const sourceOrder = orders.orders.find((o) => o.order.id === orderIdToImport)?.order
  const sourceLabel = sourceOrder ? (SampleConfigurationTypes[sourceOrder.sampleConfiguration]?.label ?? sourceOrder.sampleConfiguration) : undefined

  const selectedPoolItems = useMemo(
    () =>
      [...Object.keys(selectedSamples), ...Object.keys(selectedNCs)]
        .filter((key) => selectedSamples[key] || selectedNCs[key])
        .map((sampleId) =>
          samplePools
            .map((pool) => [...pool.samples, ...pool.negativeControls].find((element) => element.id == sampleId))
            .find((element) => !!element)
        )
        .filter((item): item is FileForProcessing => !!item),
    [selectedSamples, selectedNCs, samplePools]
  )

  // Merge is a physical property of the sample files: a merged single-file sample cannot become an
  // unmerged pair (or vice-versa), so importing across a merge boundary is impossible. Block it
  // outright — the backend rejects it too. Detected per-sample so it works for typed-in source ids.
  const mergeMismatch = selectedPoolItems.some((item) => item.isMerged !== targetIsMerged)
  // Trimming, on the other hand, can be applied on import — warn and require acknowledgement only.
  const trimWillBeConverted = selectedPoolItems.some((item) => item.isTrimmed !== targetIsTrimmed)
  const mustAcknowledge = trimWillBeConverted && !acknowledgeModeConversion
  const importBlocked = mergeMismatch || mustAcknowledge

  useEffect(() => {
    /* reset sample pool to first available */
    setTargetSamplePool(0)
  }, [samplePools])

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedOrderSearch(orderSearch), 400)
    return () => clearTimeout(handle)
  }, [orderSearch])

  /*
   * The orders endpoint is paginated and clamps `size` to 100, so the dropdown can never hold every
   * order of a long-lived org - asking for more just silently truncates. Search server-side (same
   * shape as OrderSearchWidget) so orders past the first page stay reachable.
   */
  useEffect(() => {
    dispatch(
      Actions.order.getAllOrderRequest({
        size: 100,
        page: 0,
        filters: [
          {
            value: debouncedOrderSearch,
            parameter: 'name',
            filterType: 'SUBSTR'
          },
          ...(filterByTaxa
            ? [
                {
                  value: props.order.targetTaxonomy,
                  parameter: 'targetTaxonomy',
                  filterType: FilterTypes.SUBSTR.key
                }
              ]
            : [])
        ]
      })
    )
  }, [filterByTaxa, debouncedOrderSearch])

  const handleFetchPools = async () => {
    if (!isValidUUID(orderIdToImport)) {
      console.error('not a valid uuid')
      setError('Order id is not a valid uuid. Please verify')
      return
    }
    try {
      setError(null) // Clear any previous error
      const response = await tracingAxios.get<ISamplePool[]>(`${Environment.API_URL}/api/orders/${orderIdToImport}/pools`, {
        withCredentials: true
      })
      setSamplePools(response.data)
    } catch (err) {
      console.error('Error fetching sample pools:', err)
      setError('Failed to fetch sample pools. Please check the Order ID and try again.')
    }
  }

  const contentToSelectedRecord = (newValue: boolean, ids: { id: string }[]) => {
    return ids.reduce((p1, p2) => ({ ...p1, [p2.id]: newValue }), {})
  }

  const handleTargetSamplePool = (v: ChangeEvent<HTMLSelectElement>) => {
    setTargetSamplePool(Number(v.target.value))
  }

  const handleImport = async () => {
    setInProgress(true)

    const fileIdMap: Record<string, string> = {}
    const payloads = [...Object.keys(selectedSamples), ...Object.keys(selectedNCs)]
      .filter((key) => selectedSamples[key] || selectedNCs[key])
      .map((sampleId) => {
        const poolItem: FileForProcessing | undefined = samplePools
          .map((pool) => [...pool.samples, ...pool.negativeControls].find((element) => element.id == sampleId))
          .find((element) => !!element)
        if (poolItem) {
          fileIdMap[poolItem.forwardFileId] = poolItem.id
          return {
            itemType: poolItem.itemType,
            forwardFileId: poolItem.forwardFileId,
            reverseFileId: poolItem.reverseFileId,
            forwardPrimer: copyPrimers ? poolItem.forwardPrimer : undefined,
            reversePrimer: copyPrimers ? poolItem.reversePrimer : undefined
            // Merge state is not sent: the backend derives it from the files (forward+reverse = unmerged,
            // forward only = merged) and rejects the import if it does not match this order's merge
            // configuration. isTrimmed is likewise derived from this order's config; the acknowledgement
            // warns about it.
          }
        } else {
          console.error("Unexpected issue: Couldn't resolve poolItem with id ", sampleId)
          setError(`Unexpected error: Could not resolve poolItem with id ${sampleId}`)
        }
      })
      .filter((item) => !!item)

    const metadatas = [...Object.keys(selectedSamples), ...Object.keys(selectedNCs)]
      .filter((key) => selectedSamples[key] || selectedNCs[key])
      .map((sampleId) => {
        const poolItem: FileForProcessing | undefined = samplePools
          .map((pool) => [...pool.samples, ...pool.negativeControls].find((element) => element.id == sampleId))
          .find((element) => !!element)
        if (poolItem) {
          return poolItem.sampleMetadata
        } else {
          console.error("Unexpected issue: Couldn't resolve poolItem with id ", sampleId)
          setError(`Unexpected error: Could not resolve poolItem with id ${sampleId}`)
        }
      })
      .filter((item) => !!item)
      .map((item) => ({ ...item, id: undefined, orderId: undefined, version: undefined }))

    let samplePoolIdToAddFilesTo = targetSamplePool !== undefined ? targetPools[targetSamplePool]?.id : undefined
    if (importAsNewSamplePool) {
      try {
        const newPool = await tracingAxios.post<ISamplePool>(
          `${Environment.API_URL}/api/orders/${props.order.id}/pools/`,
          {},
          { withCredentials: true }
        )
        samplePoolIdToAddFilesTo = newPool.data.id
      } catch (e) {
        console.error(e)
        setError('' + e)
      }
    }

    const sampleIdMap: Record<string, string> = {}

    for (const payload of payloads) {
      try {
        const response = await tracingAxios.post<FileForProcessing>(
          `${Environment.API_URL}/api/orders/${props.order.id}/pools/${samplePoolIdToAddFilesTo}/contents/`,
          payload,
          {
            withCredentials: true
          }
        )
        if (fileIdMap[payload.forwardFileId]) {
          sampleIdMap[fileIdMap[payload.forwardFileId]] = response.data.id
        }
      } catch (e) {
        console.error(e)
        console.error('for sample', payload)
        setError(`Could not sample for file id ${payload.forwardFileId}`)
      }
    }

    if (copyMetadata) {
      const payload = metadatas
        .map((metadata) => {
          if (sampleIdMap[metadata.sampleId ?? '']) {
            return { ...metadata, sampleId: sampleIdMap[metadata.sampleId ?? ''] }
          } else {
            return null
          }
        })
        .filter((x) => !!x)
      try {
        await tracingAxios.post(`${Environment.API_URL}/api/orders/${props.order.id}/sample-metadata/`, payload, {
          withCredentials: true
        })
      } catch (e) {
        console.error(e)
        console.error('for sample metadata')
        setError(`Could not add metadata`)
      }
    }

    setInProgress(false)
    setIsImportCompleted(true)
    dispatch(Actions[orderSlice.name].getAllDatasetRequest({ orderId: props.order.id }))
  }

  const handleOrderChosen = (
    newValue: SingleValue<{
      value: string
      label: string
      targetTaxonomy: string
    }>
  ) => {
    if (newValue == null) return
    setOrderIdToImport(newValue.value)
  }

  return (
    <BaseModal isOpen={props.isOpen} testId="importSamplesModal" onClose={props.onClose}>
      <div className="modal-box max-w-full max-h-[80%]">
        <h3 className="text-lg font-bold">Import Samples from another Order to Samplepool</h3>
        {error && <p className="text-red-500 mt-2">{error}</p>}
        {isImportCompleted && (
          <div className="my-4">
            <span>Your import has completed. Close this dialog to continue.</span>
          </div>
        )}
        {!isImportCompleted && (
          <>
            <div className="my-4 flex flex-row gap-2">
              <Select
                className="w-full max-w-sm import-select-order"
                placeholder="Search orders by name..."
                options={orders.orders.map((o) => ({
                  value: o.order.id,
                  label: o.order.name,
                  targetTaxonomy: o.order.targetTaxonomy
                }))}
                /* The list is already narrowed server-side; react-select's client-side filter would
                   only hide options again on a stale input value. */
                filterOption={null}
                onInputChange={(value) => setOrderSearch(value)}
                formatOptionLabel={(option) => (
                  <div className="flex items-center gap-2">
                    <AmpliconWidget amplicon={option.targetTaxonomy} showLabel={false} />
                    <span>{option.label}</span>
                  </div>
                )}
                styles={{
                  menu: (provided) => ({
                    ...provided,
                    maxWidth: '100%',
                    overflowX: 'hidden'
                  }),
                  option: (provided) => ({
                    ...provided,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  })
                }}
                onChange={handleOrderChosen}
              />
              <label className="label">
                <input
                  type="checkbox"
                  className="checkbox mr-2"
                  checked={filterByTaxa}
                  onChange={(v) => setFilterByTaxa(v.target.checked)}
                />
                Show Only Orders with same Taxa
              </label>
            </div>
            <div className="text-sm bg-base-200 rounded-box p-3 my-2 space-y-1">
              <div>
                This order&apos;s sample format: <b>{targetLabel}</b>
              </div>
              {sourceLabel && (
                <div>
                  Source order&apos;s sample format: <b>{sourceLabel}</b>
                </div>
              )}
              <div className="opacity-70">Imported samples are always stored using this order&apos;s sample format.</div>
            </div>
            <div>
              <label className="label">
                <span className="label-text">Order ID to import from</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Enter Order ID"
                  className="input input-bordered flex-1"
                  value={orderIdToImport}
                  onChange={(e) => setOrderIdToImport(e.target.value)}
                />
                <button data-testid="fetch-pools" className="btn btn-primary" onClick={handleFetchPools}>
                  Fetch
                </button>
              </div>
              <hr className="my-1" />
              <div className="gap-2"></div>
            </div>
          </>
        )}
        {!isImportCompleted && (
          <div className="my-4 space-y-2 overflow-y-auto max-h-96">
            <div className="flex flex-col gap-2">
              <div className="flex space-x-2 ">
                <input
                  className={`checkbox ${targetPools.length == 0 && 'input-disabled'}`}
                  disabled={targetPools.length == 0}
                  type="checkbox"
                  checked={importAsNewSamplePool}
                  onChange={(v: ChangeEvent<HTMLInputElement>) => setImportAsNewSamplepool(v.target.checked)}
                />
                <span> Import as a new Samplepool</span>
                <b>{' or '}</b>
                <span> select SamplePool Index to import to</span>
                <select
                  data-testid="select-pool-index"
                  className={`select select-sm ${!!importAsNewSamplePool && 'select-disabled'}`}
                  onChange={handleTargetSamplePool}
                >
                  {targetPools.map((_sp, index) => (
                    <option key={index}>{index}</option>
                  ))}
                </select>
              </div>
              <div className="flex space-x-2 ">
                <input
                  data-testId="import-primers"
                  className="checkbox"
                  type="checkbox"
                  checked={copyPrimers}
                  onChange={(v) => setCopyPrimers(v.target.checked)}
                />
                <span>Import Primers</span>
              </div>
              <div className="flex space-x-2 ">
                <input
                  data-testid="import-metadata"
                  className="checkbox"
                  type="checkbox"
                  checked={copyMetadata}
                  onChange={(v) => setCopyMetadata(v.target.checked)}
                />
                <span>Import Metadata</span>
              </div>
            </div>
            {samplePools.map((pool) => (
              <div key={pool.id} tabIndex={0} className="collapse collapse-arrow border border-base-300 rounded-box">
                <input data-testid="pool-collapse" type="checkbox" />
                <div className="collapse-title text-lg font-medium">
                  Pool {pool.id} ({pool.samples.length} Samples / {pool.negativeControls.length} NCs )
                </div>

                <div className="collapse-content">
                  <div className="my-1 flex items-center space-x-2 ">
                    <span
                      data-testid={'select-all-samples'}
                      onClick={() => setSelectedSamples(contentToSelectedRecord(true, pool.samples))}
                      className="underline cursor-pointer"
                    >
                      Select all Samples
                    </span>
                    <span
                      onClick={() => setSelectedSamples(contentToSelectedRecord(false, pool.samples))}
                      className="underline cursor-pointer"
                    >
                      Deselect all Samples
                    </span>
                  </div>
                  <div className="my-1 flex items-center space-x-2 ">
                    <span
                      data-testid={'select-all-ncs'}
                      onClick={() => setSelectedNCs(contentToSelectedRecord(true, pool.negativeControls))}
                      className="underline cursor-pointer"
                    >
                      Select all NegativeControls
                    </span>
                    <span
                      onClick={() => setSelectedNCs(contentToSelectedRecord(false, pool.negativeControls))}
                      className="underline cursor-pointer"
                    >
                      Deselect all NegativeControls
                    </span>
                  </div>
                  {pool.samples.map((sample) => (
                    <div key={sample.id} className="flex items-center space-x-2 my-1">
                      <input
                        type="checkbox"
                        className="checkbox"
                        checked={!!selectedSamples[sample.id]}
                        onChange={() =>
                          setSelectedSamples((previous) => {
                            return { ...previous, [sample.id]: !previous[sample.id] }
                          })
                        }
                      />
                      <span>
                        Sample | Forward: {sample.forwardFile?.fileName || 'N/A'} | Reverse: {sample.reverseFile?.fileName || 'N/A'}
                      </span>
                    </div>
                  ))}
                  {pool.negativeControls.map((nc) => (
                    <div key={nc.id} className="flex items-center space-x-2 my-1">
                      <input
                        type="checkbox"
                        className="checkbox"
                        checked={!!selectedNCs[nc.id]}
                        onChange={() =>
                          setSelectedNCs((previous) => {
                            return { ...previous, [nc.id]: !previous[nc.id] }
                          })
                        }
                      />
                      <span>
                        NegativeControl | Forward: {nc.forwardFile?.fileName || 'N/A'} | Reverse: {nc.reverseFile?.fileName || 'N/A'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        {!isImportCompleted && mergeMismatch && (
          <div className="alert alert-error my-2 flex items-start gap-2" data-testid="merge-mismatch-error">
            <span>
              Nope — this won&apos;t work. Some selected samples have a different <b>merge</b> setting than this order
              (this order is <b>{targetIsMerged ? 'merged' : 'non-merged'}</b>). Merged and non-merged samples are
              physically different and cannot be imported into each other. Deselect those samples, or import into an
              order with a matching format (<b>{targetLabel}</b>).
            </span>
          </div>
        )}
        {!isImportCompleted && !mergeMismatch && trimWillBeConverted && (
          <div className="alert alert-warning my-2 flex items-start gap-2" data-testid="mode-conversion-warning">
            <input
              type="checkbox"
              className="checkbox mt-1"
              data-testid="acknowledge-mode-conversion"
              checked={acknowledgeModeConversion}
              onChange={(v) => setAcknowledgeModeConversion(v.target.checked)}
            />
            <span>
              Some selected samples have a different <b>trimming</b> setting than this order. They will be <b>converted</b> to
              this order&apos;s format (<b>{targetLabel}</b>) on import. I acknowledge this.
            </span>
          </div>
        )}
        <div className="modal-action">
          {!isImportCompleted && (
            <button
              data-testid={'import-samples-btn'}
              onClick={handleImport}
              className={`btn btn-primary ${inProgress || importBlocked || Object.keys({ ...selectedSamples, ...selectedNCs }).filter((s) => !!{ ...selectedSamples, ...selectedNCs }[s]).length == 0 ? 'btn-disabled' : ''}`}
              disabled={inProgress || importBlocked}
            >
              Import
            </button>
          )}
          <button data-testid={'close-import-modal'} onClick={() => props.onClose()} className="btn btn-outline">
            Close
          </button>
        </div>
      </div>
    </BaseModal>
  )
}
